!customElements.get("op-algolia-moco-picker") && customElements.define("op-algolia-moco-picker", class extends HTMLElement {
    connectedCallback () {
        this.sectionId = this.dataset.sectionId;
        this.shopRoot = window.Shopify?.routes?.root || "/";
        this.groupKey = this.dataset.groupKey;
        this.currentProductHandle = this.dataset.currProductHandle;
        this.hoveringProduct = null;
        this.thumbnails = [];
        this.thumbnailsLimit = parseInt(this.dataset.thumbnailsLimit || "5");
        this.itemTemplate = this.querySelector("[data-moco-picker-item]");
        this.selItemTemplate = this.querySelector("[data-selected-moco-item]");
        this.moreModalBtn = this.querySelector("[data-more-modal-btn]");
        this.moreCountDisplay = this.moreModalBtn?.querySelector("[data-remaining-count]");
        this.hoverImages = document.querySelectorAll('.js-hover-galley-img');
        this.mainGalleryImg = document.querySelector(".js-media-img img");
        this.thumbEnterHandler = this.onThumbMouseEnter.bind(this);
        this.thumbLeaveHandler = this.onThumbMouseLeave.bind(this);
        this.hoverTimer = null;
        this.hits = [];
        this.classicPicker = this.dataset.classicPickerId ? document.getElementById(this.dataset.classicPickerId) : null;
        this.showingClassicPicker = false;

        
        this.thumbnailsFromMetafield = this.querySelectorAll("[data-handle]");

        this.dynamicElements = {
            price: document.querySelector(`#op-main-price-${this.sectionId}`),
            compareAtPrice: document.querySelector(`#op-main-compare-${this.sectionId}`),
            frame: document.querySelector(`#js-frame-info-${this.sectionId} .subtitle`),
            lens: document.querySelector(`#js-lens-info-${this.sectionId} .subtitle`),
        }

        this.compareWrappers = document.querySelectorAll(`.js-compare-wrapper-${this.sectionId}`);
        this.promoBadges = document.querySelectorAll(`.js-promoBadge-${this.sectionId}`);

        this.mobileDesktopLimit = parseInt(this.dataset.mdLimit || "4");
        this.tabletLimit = parseInt(this.dataset.tbLimit || "6");
        // Same range as the 7-column tablet grid in the snippet (`mdrange:tw-grid-cols-7`,
        // 601-1023px): 6 tiles plus the "+N" button fill the 7 columns exactly.
        this.mqTablet = window.matchMedia("(min-width: 601px) and (max-width: 1023px)");

        this.setLimitByViewport = () => {
            this.thumbnailsLimit = this.mqTablet.matches ? this.tabletLimit : this.mobileDesktopLimit;
        };

        this.onViewportChange = () => {
            const prev = this.thumbnailsLimit;
            this.setLimitByViewport();
            if (prev !== this.thumbnailsLimit) this.renderThumbs();
        };

        this.setLimitByViewport();
        this.mqTablet.addEventListener?.("change", this.onViewportChange);

        if (this.groupKey) {
            this.loadGroupItems();
        }

        this.onDkCodesChanged = () => {
            if (!this.currentProductHandle) return;
            const thumb = this.querySelector(`[data-handle="${this.currentProductHandle}"]`);
            this.updateProductInfo(this.currentProductHandle, thumb);
        };
        document.addEventListener("po:dk:discount-codes-changed", this.onDkCodesChanged);
    }

    disconnectedCallback () {
        if (this.onDkCodesChanged) {
            document.removeEventListener("po:dk:discount-codes-changed", this.onDkCodesChanged);
        }
        this.mqTablet?.removeEventListener?.("change", this.onViewportChange);
    }

    clearThumbs () {
        this.thumbnails.forEach(thumb => thumb.remove());
        this.thumbnails.length = 0;
    }

    ensureTemplateLoaded (container, templateSelector, loadedKey) {
        if (!container || container.dataset?.[loadedKey] === "true") return;

        const template = container.querySelector(templateSelector);
        if (!template) return;

        container.appendChild(template.content.cloneNode(true));
        template.remove();
        container.dataset[loadedKey] = "true";
    }

    ensureThumbImageLoaded (thumb) {
        this.ensureTemplateLoaded(thumb, "template[data-moco-image-template]", "mocoImageLoaded");
    }

    filterHits (hits = []) {
        const handleSet = new Set();
        const filteredHits = [];

        for (const hit of hits) {
            if (handleSet.has(hit.handle)) {
                continue;
            }

            filteredHits.push({
                handle: hit.handle,
                id: hit.id,
                image: hit.image,
                inventory_available: hit.inventory_available,
                objectID: hit.objectID,
                title: hit.title,
            });

            handleSet.add(hit.handle);
        }

        return filteredHits;
    }

    formatImageUrl (url, width, height) {
        const parsedUrl = URL.parse(url, window.location.origin);

        if (!parsedUrl) {
            console.error("Invalid URL:", url);
            return url;
        }

        if (width) {
            parsedUrl.searchParams.set("width", width);
        }

        if (height) {
            parsedUrl.searchParams.set("height", height);
        }

        return parsedUrl.toString();
    }

    isFreshHandoff (group) {
        // The localStorage mirror is cross-tab and outlives the PLP click, so it is only
        // trusted for the navigation it was written for: a short freshness window.
        return typeof group?.ts === "number" && Date.now() - group.ts < 30000;
    }

    loadGroupItems () {
        const belongsToThisGroup = (group) => Array.isArray(group?.hits) && group.hits.length > 0 && group.xGroupKey === this.groupKey;
        let lastGroup = null;

        try {
            lastGroup = JSON.parse(sessionStorage.getItem("lastGroupItem"));
        }
        catch (error) {
            console.error("Error parsing lastGroupItem session storage:", error);
        }

        // A PDP opened in a NEW tab (middle-click, right-click → "Open in new tab") never
        // inherits the PLP tab's sessionStorage: fall back to the localStorage mirror.
        // The tab can also carry an older journey through another group, so the mirror is
        // consulted whenever the session entry is not the one for this group.
        if (!belongsToThisGroup(lastGroup)) {
            try {
                const mirror = JSON.parse(localStorage.getItem("lastGroupItem"));

                if (belongsToThisGroup(mirror) && this.isFreshHandoff(mirror)) {
                    lastGroup = mirror;
                }
            }
            catch (error) {
                console.error("Error parsing lastGroupItem local storage:", error);
            }
        }

        const hitsFound = belongsToThisGroup(lastGroup);

        if (hitsFound) {
            this.hits = lastGroup.hits;
        }

        // Keep the PLP order for the whole in-group journey: clicking a MOCO tile leads to
        // a sibling variant PDP, which has to carry on cycling through the same sequence.
        // An entry for another group is deliberately left untouched — it belongs to a
        // journey the user may come back to (a detour through a related product must not
        // wipe it), and the group check above already stops it from being applied here.
        // Storage access is guarded: where storage itself is blocked (cookies disabled,
        // sandboxed iframe) the picker must still render.
        if (hitsFound) {
            try {
                sessionStorage.setItem("lastGroupItem", JSON.stringify(lastGroup));
            }
            catch (error) {
                // Storage access blocked: the current render still works.
            }
        }

        let mirror = null;
        let dropMirror = true;

        try {
            mirror = JSON.parse(localStorage.getItem("lastGroupItem"));

            // A fresh mirror for ANOTHER group may belong to a tab that has not loaded
            // yet (multiple cards opened in background tabs): leave it for that tab.
            dropMirror = !mirror || mirror.xGroupKey === this.groupKey || !this.isFreshHandoff(mirror);
        }
        catch (error) {
            // Unreadable/corrupt mirror: drop it below if storage allows.
        }

        if (dropMirror) {
            try {
                localStorage.removeItem("lastGroupItem");
            }
            catch (error) {
                // Storage access blocked: nothing to clean up.
            }
        }

        this.renderThumbs();

        //this.toggleClassicPicker(!hitsFound);
    }

    onThumbMouseEnter (event) {
        const handle = event.currentTarget.dataset.handle;
        
        if (this.currentProductHandle !== handle) {
            this.hoveringProduct = true;
    
            clearTimeout(this.hoverTimer);
            this.reduceElementsOpacity(true);
    
            this.hoverTimer = setTimeout(() => {
                this.updateProductInfo(handle, event.currentTarget);
                this.reduceElementsOpacity(false);
            }, 300);
        }
    }

    onThumbMouseLeave () {
        if (this.hoveringProduct) {
            clearTimeout(this.hoverTimer);
            this.hoverTimer = null;
            this.hoveringProduct = false;
    
            this.reduceElementsOpacity(true);
    
            this.hoverTimer = setTimeout(() => {
                const currentThumb = this.querySelector(`[data-handle="${this.currentProductHandle}"]`);
                this.updateProductInfo(this.currentProductHandle, currentThumb);
                this.reduceElementsOpacity(false);
            }, 300);
        }
    }

    reduceElementsOpacity (status) {
        for (let key in this.dynamicElements) {
            const el = this.dynamicElements[key];

            if (el) {
                status ? el.style.opacity = 0.5 : el.style.removeProperty("opacity");
            }
        }
    }

    attachThumbListeners (thumbnails = []) {
        thumbnails.forEach((thumb) => {
            if (thumb?.dataset?.hoverListenersAttached === "true") return;

            thumb.addEventListener("mouseenter", this.thumbEnterHandler);
            thumb.addEventListener("mouseleave", this.thumbLeaveHandler);
            thumb.dataset.hoverListenersAttached = "true";
        });
    }

    sortByCanonicalIndex (thumbnails) {
        // `data-moco-index` is the variant's position in the VariantsHandles metafield.
        // Missing/invalid values sort last, so a page cached before the attribute existed
        // simply keeps the server order.
        const canonicalIndex = (thumb) => {
            const parsed = parseInt(thumb.dataset.mocoIndex, 10);
            return Number.isInteger(parsed) && parsed >= 0 ? parsed : Number.MAX_SAFE_INTEGER;
        };

        return thumbnails.slice().sort((a, b) => canonicalIndex(a) - canonicalIndex(b));
    }

    // Cyclic rotation: the current variant moves to the first slot and every other tile
    // keeps its relative position, wrapping around. Returns null when the current variant
    // is not part of the sequence, so the caller can fall back.
    rotateToCurrent (thumbnails) {
        const selectedIndex = thumbnails.findIndex(thumb => thumb.dataset.handle === this.currentProductHandle);

        if (selectedIndex === -1) return null;

        return selectedIndex > 0
            ? thumbnails.slice(selectedIndex).concat(thumbnails.slice(0, selectedIndex))
            : thumbnails;
    }

    /*
     * Resolves the tile sequence as a cyclic rotation of a base order that every PDP of
     * the group resolves identically, so each hop continues the same cycle:
     *   - base = the PLP box order followed by the variants that box did not contain,
     *     when the user came from a PLP (the order is kept in storage for the whole
     *     in-group journey);
     *   - base = the VariantsHandles metafield order otherwise.
     * Since the base always covers every tile, the rotation puts the current variant
     * first and the remaining tiles keep their relative order, wrapping around.
     */
    resolveOrderedThumbnails () {
        const allThumbnails = Array.from(this.thumbnailsFromMetafield);
        const handleOrder = Array.isArray(this.hits) ? this.hits.map(hit => hit.handle) : [];
        let base;

        if (handleOrder.length) {
            // The PLP box is collection-filtered, so it can hold only part of the group.
            // The variants it left out follow it in metafield order: base has to stay a
            // single sequence covering every tile, otherwise landing on one of those
            // variants would switch base and the cycle would jump instead of continuing.
            const isInBox = (thumb) => handleOrder.indexOf(thumb.dataset.handle) !== -1;

            base = allThumbnails
                .filter(isInBox)
                .sort((a, b) => handleOrder.indexOf(a.dataset.handle) - handleOrder.indexOf(b.dataset.handle))
                .concat(this.sortByCanonicalIndex(allThumbnails.filter(thumb => !isInBox(thumb))));
        } else {
            base = this.sortByCanonicalIndex(allThumbnails);
        }

        return this.rotateToCurrent(base) || allThumbnails;
    }

    renderThumbs () {
        if (!this.itemTemplate) {
            console.error("Template not found in the component.");
            return;
        }

        const showMoreModalBtn = (thumbLength, showCount) => {
            if (!this.moreModalBtn) return;

            const remaining = Math.max(thumbLength - showCount, 0);

            if (remaining > 0 && this.moreCountDisplay) {
                this.moreCountDisplay.textContent = remaining;
                this.moreModalBtn.style.removeProperty("display");
                this.appendChild(this.moreModalBtn);
            } else {
                this.moreModalBtn.style.display = "none";
            }
        }

        /*
         * Renders the MOCO thumbnails as a cyclic rotation of the base order resolved by
         * resolveOrderedThumbnails(): the current variant first, every other tile keeping
         * its relative position. Up to `thumbnailsLimit` thumbnails render (4 on the
         * 5-column mobile/desktop grid, 6 on the 7-column tablet grid), with the "+N"
         * modal button for the rest — hidden when nothing remains.
         */
        const orderedThumbnails = this.resolveOrderedThumbnails();
        const thumbLength = orderedThumbnails.length;
        const thumbnailsToRender = orderedThumbnails.slice(0, Math.min(thumbLength, this.thumbnailsLimit));

        // Clear the previous render first: on viewport changes the limit shrinks or grows,
        // and thumbnails left behind would survive in the row (and out of order).
        this.clearThumbs();
        thumbnailsToRender.forEach((thumbnail) => {
            this.appendChild(thumbnail);
            this.ensureThumbImageLoaded(thumbnail);
        });
        this.thumbnails = thumbnailsToRender;
        showMoreModalBtn(thumbLength, thumbnailsToRender.length);
        this.attachThumbListeners(thumbnailsToRender);
    }

    toggleClassicPicker (status) {
        if (this.classicPicker) {
            this.classicPicker.style.display = status ? "block" : "none";
            status ? this.style.display = "none" : this.style.removeProperty("display");
            this.showingClassicPicker = status;
        }
    }

    updateProductInfo (handle, thumbElement) {
        const thumbnail = thumbElement || this.querySelector(`[data-handle="${handle}"]`);

        if (this.mainGalleryImg && thumbnail?.dataset?.srcset) {
            this.mainGalleryImg.setAttribute("srcset", thumbnail.dataset.srcset);
            this.mainGalleryImg.setAttribute("src", thumbnail.dataset.srcset);
        }

        let hoverElement;

        for (let el of this.hoverImages) {
            const isCurrent = el.dataset.productHandle === handle;
            el.style.opacity = isCurrent && handle !== this.currentProductHandle ? 1 : 0;

            if (isCurrent) {
                hoverElement = el;
            }
        }
        
        if (!hoverElement) return;

        const resolved = window.PoDkStorefrontPrices?.resolveDisplayFromNode?.(hoverElement);
        const priceText = resolved?.priceText ?? hoverElement.dataset.price ?? '';
        const compareText = resolved?.compareText ?? hoverElement.dataset.compare ?? '';
        const promoBadge = resolved?.promoBadge ?? hoverElement.dataset.promoBadge?.trim() ?? '';
        const hasDiscount = resolved?.hasDiscount ?? Boolean(hoverElement.dataset.compare);

        if (this.dynamicElements.price) this.dynamicElements.price.textContent = priceText;
        if (this.dynamicElements.compareAtPrice) {
            this.dynamicElements.compareAtPrice.textContent = compareText;
        }
        if (this.dynamicElements.lens) this.dynamicElements.lens.textContent = hoverElement.dataset.lens || '';
        if (this.dynamicElements.frame) this.dynamicElements.frame.textContent = hoverElement.dataset.frame || '';

        this.compareWrappers.forEach((el) => el.classList.toggle('tw-hidden', !hasDiscount));
        this.promoBadges.forEach((el) => {
            el.textContent = promoBadge;
        });
    }
});